// The video studio: draws a frame of the finished video at any moment, plays it with its sound, and records it to a video file.
import { TRANSITION, buildTimeline, cameraAt, cropFor, easeInOut, frameAt, narrationStart, subtitleChunks, wrapText } from "./timeline";
import { blinkAt, blinkSchedule, headPose, mouthAt } from "./lipsync";
import { drawPresenter } from "./presenters";
import { drawPuppet } from "./puppet";
import { drawNameCaption, drawStage, photoPlacement, presenterLayout } from "./stage";
import { drawToonBeat } from "./toon/render";

const CLOSED_MOUTH = { open: 0, wide: 0, round: 0, energy: 0, nod: 0 };

export const GRADES = {
  none: { label: "None", filter: "", tint: null },
  cinematic: { label: "Cinematic", filter: "contrast(1.1) saturate(1.12)", tint: ["rgba(0,60,90,0.16)", "rgba(255,150,60,0.14)"] },
  warm: { label: "Warm", filter: "sepia(0.22) saturate(1.15) brightness(1.03)", tint: null },
  cool: { label: "Cool", filter: "hue-rotate(-8deg) saturate(1.05) brightness(1.02)", tint: ["rgba(0,90,160,0.12)", "rgba(0,60,120,0.0)"] },
  bw: { label: "Black & white", filter: "grayscale(1) contrast(1.12)", tint: null },
};
export const CAPTION_STYLES = { off: "Off", caption: "Short caption", subtitles: "Subtitles (what is said)" };

export const FIT_STYLES = { auto: "Automatic", fill: "Fill the frame", fit: "Show the whole picture" };

export const DEFAULT_LOOK = {
  fit: "fill", grade: "cinematic", vignette: 0.35, grain: 0.05, letterbox: false, captions: "subtitles", motion: "auto", transition: "fade",
  title: "", titleSub: "", outro: "", outroSub: "", watermark: "", brandA: "#0b5ed7", brandB: "#0a2540", musicVolume: 0.16,
};

const FONT = '"Inter", "Segoe UI", Arial, sans-serif';

export function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("A picture could not be loaded."));
    img.src = url;
  });
}

// A moving clip of a scene (muted: the narration is played from the narration file, so it is always in step with the words).
export function loadVideo(url) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = "anonymous";
    video.preload = "auto";
    const fail = setTimeout(() => reject(new Error("A clip could not be loaded.")), 40000);
    video.onloadeddata = () => { clearTimeout(fail); resolve(video); };
    video.onerror = () => { clearTimeout(fail); reject(new Error("A clip could not be loaded.")); };
    video.src = url;
  });
}

export async function loadAudioBuffer(context, url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error("The narration could not be loaded.");
  return context.decodeAudioData(await response.arrayBuffer());
}

export class Studio {
  // scenes: [{ text, caption, seconds, motion, transition, img (an Image or null), audioBuffer (or null) }]
  // A scene may also have `video` (a clip element) and `clipTalking` (the clip's lips follow the narration); `useStill: true` shows the picture anyway.
  constructor(canvas, scenes, look = DEFAULT_LOOK, size = [1280, 720]) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.scenes = scenes;
    this.look = { ...DEFAULT_LOOK, ...look };
    this.setSize(size);
    this.rebuild();
    this.filtersOk = "filter" in this.ctx;
    this.subtitleCache = new Map();
    this.onVideoFrame = null; // called when a clip has moved to a new position while the video is not playing, so the frame can be drawn again
  }

  setSize([w, h]) {
    this.W = w;
    this.H = h;
    this.canvas.width = w;
    this.canvas.height = h;
    this.vignette = null;
    this.grainTile = null;
  }

  setLook(look) { this.look = { ...this.look, ...look }; this.rebuild(); }

  setScenes(scenes) { this.scenes = scenes; this.subtitleCache.clear(); this.rebuild(); }

  rebuild() {
    this.timeline = buildTimeline(this.scenes, this.look);
    this.total = this.timeline.total;
  }

  // ------------------------------------------------------------------------------------------ drawing
  // How a scene's clip is shown: [playbackRate, startsAt] - a talking clip starts with the narration at normal speed; any other clip is stretched
  // (slowed a little at most) over the scene.
  clipPlan(item, first) {
    const scene = item.scene;
    const duration = scene.video && isFinite(scene.video.duration) ? scene.video.duration : 0;
    if (scene.clipTalking) return [1, narrationStart(item, first)];
    const room = Math.max(1, item.end - item.start - TRANSITION);
    return [Math.min(1, Math.max(0.6, duration / room)), item.start];
  }

  // Whether a picture of this shape is shown whole (over a blurred copy) instead of cropped to fill the frame.
  showsWhole(iw, ih) {
    if (this.look.fit === "fit") return true;
    if (this.look.fit !== "auto") return false;
    return Math.abs(Math.log(iw / ih / (this.W / this.H))) > 0.12;
  }

  usesClip(scene) { return !!(scene.video && !scene.useStill && scene.video.readyState >= 2); }

  // Puts every clip where the timeline is: while playing they run (and are corrected when they drift); otherwise they are moved to the exact frame.
  syncVideos(time, playing) {
    this.timeline.items.forEach((item, i) => {
      const video = item.type === "scene" ? item.scene.video : null;
      if (!video || item.scene.useStill) return;
      const active = time >= item.start - 0.05 && time <= item.end + 0.05;
      if (!active) { if (!video.paused) video.pause(); return; }
      const [rate, from] = this.clipPlan(item, i === 0 || this.timeline.items[0] === item);
      const length = isFinite(video.duration) && video.duration > 0 ? video.duration : Infinity;
      const local = Math.max(0, Math.min((time - from) * rate, length - 0.04));
      if (playing) {
        if ((time - from) * rate >= length - 0.06) { // the clip has run out: it stays on its last frame (a finished video would start over if played again)
          if (!video.paused) video.pause();
          if (isFinite(length) && Math.abs(video.currentTime - (length - 0.06)) > 0.25) video.currentTime = length - 0.06;
          return;
        }
        video.playbackRate = rate;
        if (Math.abs(video.currentTime - local) > 0.3) video.currentTime = local;
        if (video.paused && time >= from) video.play().catch(() => {});
        else if (time < from && !video.paused) video.pause();
      } else {
        if (!video.paused) video.pause();
        if (Math.abs(video.currentTime - local) > 0.03) {
          if (!video.__seekHook) { video.__seekHook = true; video.addEventListener("seeked", () => { if (this.onVideoFrame) this.onVideoFrame(); }); }
          video.currentTime = local;
        }
      }
    });
  }

  pauseVideos() { this.scenes.forEach((s) => { if (s.video && !s.video.paused) s.video.pause(); }); }

  drawFrame(t, playing = false) {
    const { ctx, W, H } = this;
    const time = Math.min(Math.max(t, 0), this.total);
    this.syncVideos(time, playing);
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, W, H);
    if (this.timeline.items.length) {
      const { current, previous, progress } = frameAt(this.timeline, time);
      if (previous) {
        this.drawItem(previous, time);
        this.drawTransition(current, previous, time, progress);
      } else {
        this.drawItem(current, time);
      }
    }
    this.drawOverlays(time);
    ctx.restore();
  }

  drawTransition(item, previous, time, p) {
    const { ctx, W, H } = this;
    const kind = item.type === "scene" ? item.scene.transition || this.look.transition : this.look.transition;
    ctx.save();
    if (kind === "dip") {
      ctx.fillStyle = "#000";
      ctx.globalAlpha = p < 0.5 ? Math.min(1, p * 2) : 1;
      ctx.fillRect(0, 0, W, H);
      ctx.globalAlpha = p < 0.5 ? 0 : (p - 0.5) * 2;
      if (p >= 0.5) this.drawItem(item, time);
    } else if (kind === "slide") {
      ctx.translate(W * (1 - p), 0);
      ctx.globalAlpha = 1;
      this.drawItem(item, time);
    } else if (kind === "wipe") {
      ctx.beginPath();
      ctx.rect(0, 0, W * p, H);
      ctx.clip();
      this.drawItem(item, time);
    } else if (kind === "zoom") {
      ctx.globalAlpha = p;
      ctx.translate(W / 2, H / 2);
      const s = 1.28 - 0.28 * p;
      ctx.scale(s, s);
      ctx.translate(-W / 2, -H / 2);
      this.drawItem(item, time);
    } else {
      ctx.globalAlpha = p;
      this.drawItem(item, time);
    }
    ctx.restore();
  }

  drawItem(item, time) {
    const u = (time - item.start) / (item.end - item.start);
    if (item.type === "scene") this.drawScene(item, time, u);
    else this.drawCard(item, u);
  }

  // A scene with a presenter: the stage, then the person speaking the scene's narration. `scene.presenter` is shared by all the scenes of the video:
  //   { kind: "builtin", spec } or { kind: "photo", img, geometry, skin, hasAlpha }, plus { stage, bg (the stage picture, if any), name, role, seed }
  // and each scene has its own `analysis` (analyseSpeech of its narration), so the lips follow the sound that is really played.
  drawPresenterScene(item, time, u) {
    const { ctx, W, H } = this;
    const scene = item.scene;
    const p = scene.presenter;
    const first = item.sceneIndex === 0 && this.timeline.items[0] === item;
    const speech = scene.analysis ? mouthAt(scene.analysis, time - narrationStart(item, first)) : CLOSED_MOUTH;
    if (!p.blinks) p.blinks = blinkSchedule(p.seed || 1, 900);
    const state = { mouth: speech, blink: blinkAt(p.blinks, time), pose: headPose(time, speech, p.seed || 1) };
    const stage = p.stage;
    if (p.kind === "photo" && p.img) {
      const iw = p.img.naturalWidth || p.img.width;
      const ih = p.img.naturalHeight || p.img.height;
      const place = photoPlacement(iw, ih, W, H, stage, p.hasAlpha);
      if (place.cutout) {
        drawStage(ctx, W, H, stage, p.bg, time);
      } else if (place.whole) {
        const back = cropFor(iw, ih, W, H, { zoom: 1.08 });
        ctx.save();
        if (this.filtersOk) ctx.filter = `blur(${Math.round(Math.min(W, H) * 0.035)}px) brightness(0.62)`;
        ctx.drawImage(p.img, back[0], back[1], back[2], back[3], -W * 0.03, -H * 0.03, W * 1.06, H * 1.06);
        ctx.restore();
      }
      drawPuppet(ctx, p.img, p.geometry, state, place, p.skin || undefined, place.cutout ? 1 : 0.4);
    } else {
      drawStage(ctx, W, H, stage, p.bg, time);
      drawPresenter(ctx, p.spec, state, presenterLayout(W, H, stage));
    }
    drawNameCaption(ctx, W, H, p.name || "", p.role || "", time, stage.colorA);
    this.drawCaption(item, time, u);
  }

  // A cartoon beat: the characters and the setting, the lips of the speaker following the voice that is really played. `scene.toon` = see toon/render.js.
  drawToonScene(item, time, u) {
    const scene = item.scene;
    const first = item.sceneIndex === 0 && this.timeline.items[0] === item;
    const start = narrationStart(item, first);
    const speech = scene.analysis ? mouthAt(scene.analysis, time - start) : CLOSED_MOUTH;
    drawToonBeat(this.ctx, this.W, this.H, scene.toon, Math.max(0, time - item.start), time, speech);
    this.drawCaption(item, time, u);
  }

  drawScene(item, time, u) {
    const { ctx, W, H, look } = this;
    const scene = item.scene;
    if (scene.toon) { this.drawToonScene(item, time, u); return; }
    if (scene.presenter && !this.usesClip(scene)) { this.drawPresenterScene(item, time, u); return; }
    const clip = this.usesClip(scene);
    const source = clip ? scene.video : scene.img;
    if (source) {
      let cam = cameraAt(scene.motion || look.motion, u, item.sceneIndex);
      if (clip) cam = { zoom: 1 + (cam.zoom - 1) * 0.3, x: cam.x * 0.25, y: cam.y * 0.25 }; // a moving clip needs only a little camera on top
      const iw = clip ? source.videoWidth : source.naturalWidth || source.width;
      const ih = clip ? source.videoHeight : source.naturalHeight || source.height;
      const grade = GRADES[look.grade] || GRADES.none;
      ctx.save();
      ctx.imageSmoothingQuality = "high";
      if (this.showsWhole(iw, ih)) {
        // the whole picture, nothing cropped: over a dark, blurred copy of itself that fills the rest of the frame
        const back = cropFor(iw, ih, W, H, { zoom: 1.08 });
        if (this.filtersOk) ctx.filter = `${grade.filter ? `${grade.filter} ` : ""}blur(${Math.round(Math.min(W, H) * 0.035)}px) brightness(0.62)`;
        ctx.drawImage(source, back[0], back[1], back[2], back[3], -W * 0.03, -H * 0.03, W * 1.06, H * 1.06);
        if (this.filtersOk) ctx.filter = grade.filter || "none";
        const k = Math.min(W / iw, H / ih) * (1 + (cam.zoom - 1) * 0.6);
        const dw = iw * k;
        const dh = ih * k;
        ctx.drawImage(source, (W - dw) / 2 + cam.x * W * 0.02, (H - dh) / 2 + cam.y * H * 0.02, dw, dh);
      } else {
        const [sx, sy, sw, sh] = cropFor(iw, ih, W, H, cam);
        if (this.filtersOk && grade.filter) ctx.filter = grade.filter;
        ctx.drawImage(source, sx, sy, sw, sh, 0, 0, W, H);
      }
      ctx.restore();
      if (grade.tint) {
        const g = ctx.createLinearGradient(0, 0, 0, H);
        g.addColorStop(0, grade.tint[0]);
        g.addColorStop(1, grade.tint[1]);
        ctx.save();
        ctx.globalCompositeOperation = "soft-light";
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
      }
    } else {
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, "#1f2937");
      g.addColorStop(1, "#111827");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);
    }
    this.drawCaption(item, time, u);
  }

  drawCaption(item, time, u) {
    const mode = this.look.captions;
    if (mode === "off") return;
    const scene = item.scene;
    let text = "";
    let fade = 1;
    if (mode === "caption") {
      text = scene.caption || "";
      const local = time - item.start;
      fade = Math.min(1, local / 0.5, (item.end - time) / 0.5);
    } else {
      const start = narrationStart(item, item.sceneIndex === 0 && this.timeline.items[0] === item);
      const spoken = scene.audioBuffer ? scene.audioBuffer.duration : Math.max(0, (scene.seconds || 0) - 1);
      const key = `${scene.text}|${spoken.toFixed(2)}`;
      if (!this.subtitleCache.has(key)) this.subtitleCache.set(key, subtitleChunks(scene.text, spoken, this.H > this.W ? 5 : 8));
      const at = time - start;
      const chunk = this.subtitleCache.get(key).find((c) => at >= c.from && at < c.to);
      if (!chunk) return;
      text = chunk.text;
      fade = Math.min(1, (at - chunk.from) / 0.18 + 0.15, (chunk.to - at) / 0.18 + 0.15);
    }
    if (!text) return;
    const { ctx, W, H } = this;
    const size = Math.round(Math.min(W, H) * (mode === "caption" ? 0.052 : 0.044));
    ctx.save();
    ctx.globalAlpha *= Math.max(0, Math.min(1, fade));
    ctx.font = `700 ${size}px ${FONT}`;
    const maxW = W * (H > W ? 0.84 : 0.72);
    const lines = wrapText((s) => ctx.measureText(s).width, text, maxW).slice(0, 3);
    const lineH = size * 1.28;
    const boxW = Math.max(...lines.map((l) => ctx.measureText(l).width)) + size * 1.4;
    const boxH = lines.length * lineH + size * 0.7;
    const bottom = this.look.letterbox ? (H - W / 2.39) / 2 + H * 0.05 : H * 0.07;
    const x = (W - boxW) / 2;
    const y = H - bottom - boxH + (mode === "caption" ? (1 - Math.min(1, (time - item.start) / 0.5)) * size * 0.6 : 0);
    ctx.fillStyle = "rgba(8,10,16,0.62)";
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(x, y, boxW, boxH, size * 0.4) : ctx.rect(x, y, boxW, boxH);
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(0,0,0,0.5)";
    ctx.shadowBlur = size * 0.2;
    lines.forEach((line, i) => ctx.fillText(line, W / 2, y + size * 0.35 + lineH * (i + 0.5)));
    ctx.restore();
  }

  drawCard(item, u) {
    const { ctx, W, H, look } = this;
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, look.brandA);
    g.addColorStop(1, look.brandB);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    const glow = ctx.createRadialGradient(W * 0.75, H * 0.2, 0, W * 0.75, H * 0.2, W * 0.6);
    glow.addColorStop(0, "rgba(255,255,255,0.16)");
    glow.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
    const e = easeInOut(Math.min(1, u * 3));
    const out = Math.min(1, (1 - u) * 6);
    ctx.save();
    ctx.globalAlpha *= Math.max(0, Math.min(1, e * Math.max(0, out)));
    ctx.translate(W / 2, H / 2 + (1 - e) * H * 0.03);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#fff";
    ctx.shadowColor = "rgba(0,0,0,0.35)";
    ctx.shadowBlur = H * 0.02;
    const size = Math.round(Math.min(W, H) * 0.085);
    ctx.font = `800 ${size}px ${FONT}`;
    const lines = wrapText((s) => ctx.measureText(s).width, item.text, W * 0.8).slice(0, 4);
    const lineH = size * 1.15;
    const subSize = Math.round(size * 0.42);
    const blockH = lines.length * lineH + (item.sub ? subSize * 2 : 0);
    lines.forEach((line, i) => ctx.fillText(line, 0, -blockH / 2 + lineH * (i + 0.5)));
    if (item.sub) {
      ctx.font = `500 ${subSize}px ${FONT}`;
      ctx.globalAlpha *= 0.85;
      ctx.fillText(item.sub, 0, -blockH / 2 + lines.length * lineH + subSize * 1.2);
    }
    ctx.restore();
  }

  drawOverlays(time) {
    const { ctx, W, H, look } = this;
    if (look.vignette > 0) {
      if (!this.vignette) {
        const v = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.35, W / 2, H / 2, Math.hypot(W, H) * 0.55);
        v.addColorStop(0, "rgba(0,0,0,0)");
        v.addColorStop(1, "rgba(0,0,0,1)");
        this.vignette = v;
      }
      ctx.save();
      ctx.globalAlpha = look.vignette;
      ctx.fillStyle = this.vignette;
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }
    if (look.grain > 0) {
      if (!this.grainTile) {
        const tile = document.createElement("canvas");
        tile.width = 256;
        tile.height = 256;
        const tctx = tile.getContext("2d");
        const data = tctx.createImageData(256, 256);
        for (let i = 0; i < data.data.length; i += 4) {
          const v = Math.random() * 255;
          data.data[i] = v; data.data[i + 1] = v; data.data[i + 2] = v; data.data[i + 3] = 255;
        }
        tctx.putImageData(data, 0, 0);
        this.grainTile = tile;
      }
      ctx.save();
      ctx.globalAlpha = look.grain;
      ctx.globalCompositeOperation = "overlay";
      const ox = Math.floor((time * 977) % 256);
      const oy = Math.floor((time * 613) % 256);
      for (let x = -ox; x < W; x += 256) for (let y = -oy; y < H; y += 256) ctx.drawImage(this.grainTile, x, y);
      ctx.restore();
    }
    if (look.letterbox) {
      const bar = Math.max(0, (H - W / 2.39) / 2);
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, W, bar);
      ctx.fillRect(0, H - bar, W, bar);
    }
    if (look.watermark.trim()) {
      const size = Math.round(Math.min(W, H) * 0.027);
      ctx.save();
      ctx.font = `600 ${size}px ${FONT}`;
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";
      ctx.globalAlpha = 0.75;
      ctx.fillStyle = "#fff";
      ctx.shadowColor = "rgba(0,0,0,0.6)";
      ctx.shadowBlur = size * 0.3;
      ctx.fillText(look.watermark.trim(), W - size, H - size * (look.letterbox ? 4 : 1));
      ctx.restore();
    }
  }

  // ------------------------------------------------------------------------------------------ sound
  // Schedules the narration (and the music) on an audio context, from video time `from`; returns a function that stops it all.
  playAudio(context, destination, from = 0, music = null) {
    const nodes = [];
    const base = context.currentTime + 0.06;
    this.timeline.items.forEach((item, i) => {
      if (item.type !== "scene" || !item.scene.audioBuffer) return;
      const when = narrationStart(item, i === 0) - from;
      const source = context.createBufferSource();
      source.buffer = item.scene.audioBuffer;
      source.connect(destination);
      if (when >= 0) source.start(base + when);
      else if (-when < item.scene.audioBuffer.duration) source.start(base, -when);
      else return;
      nodes.push(source);
    });
    if (music && music.buffer) {
      const source = context.createBufferSource();
      source.buffer = music.buffer;
      source.loop = true;
      const gain = context.createGain();
      const level = this.look.musicVolume;
      gain.gain.setValueAtTime(0, base);
      gain.gain.linearRampToValueAtTime(level, base + 1.2);
      gain.gain.setValueAtTime(level, base + Math.max(1.2, this.total - from - 1.8));
      gain.gain.linearRampToValueAtTime(0, base + Math.max(1.5, this.total - from));
      source.connect(gain);
      gain.connect(destination);
      source.start(base, from % music.buffer.duration);
      nodes.push(source);
    }
    return () => nodes.forEach((n) => { try { n.stop(); } catch { /* already finished */ } });
  }
}

// Records the whole video, in real time, to a file. Resolves with a Blob. `onProgress(0..1)`.
export async function recordVideo(studio, { mime, music = null, fps = 30, bitrate = 9e6, onProgress = () => {}, signal = {} }) {
  const context = new (window.AudioContext || window.webkitAudioContext)();
  await context.resume();
  const destination = context.createMediaStreamDestination();
  const stream = studio.canvas.captureStream(fps);
  const hasAudio = studio.scenes.some((s) => s.audioBuffer) || (music && music.buffer);
  const tracks = [...stream.getVideoTracks(), ...(hasAudio ? destination.stream.getAudioTracks() : [])];
  const recorder = new MediaRecorder(new MediaStream(tracks), { mimeType: mime, videoBitsPerSecond: bitrate, audioBitsPerSecond: 160000 });
  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
  const done = new Promise((resolve, reject) => {
    recorder.onstop = () => resolve(new Blob(chunks, { type: mime.split(";")[0] }));
    recorder.onerror = (e) => reject(e.error || new Error("Recording failed."));
  });
  studio.drawFrame(0);
  recorder.start(1000);
  const stopAudio = studio.playAudio(context, destination, 0, music);
  const started = performance.now();
  const total = studio.total + 0.4;
  await new Promise((resolve) => {
    const step = () => {
      const t = (performance.now() - started) / 1000;
      if (signal.cancelled || t >= total) { resolve(); return; }
      studio.drawFrame(t, true);
      onProgress(Math.min(1, t / total));
      setTimeout(step, 1000 / fps / 1.5); // frames are drawn on a timer, so a tab in the background keeps recording
    };
    step();
  });
  stopAudio();
  studio.pauseVideos();
  studio.drawFrame(studio.total);
  await new Promise((r) => setTimeout(r, 120));
  if (recorder.state !== "inactive") recorder.stop();
  const blob = await done;
  stream.getTracks().forEach((track) => track.stop());
  context.close();
  if (signal.cancelled) throw new Error("cancelled");
  return blob;
}

export { TRANSITION };
